import {
    useCallback,
    useEffect,
    useState,
    useMemo,
    useRef
} from "react";
import {
    AccessibilityInfo,
    Platform,
    View
} from "react-native";
import type IDataTableProps from "./type";
import type {
    DataTableSelectionBarContextType,
    DataTableSelectionContextType,
    DataTableColumnVisibility,
    DataTableToolbarLayout,
    DataTableColumnFilters,
    DataTableColumnWidths,
    DataTableContextType,
    DataTableExportScope,
    DataTableExportData,
    IDataTableSortRule,
    DataTableRowModel,
    DataTableLayout
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import DataTableColumnHeader from "./components/columnHeader";
import DataTableContent from "./components/content";
import DataTableDensityToggle from "./components/densityToggle";
import DataTableExport from "./components/export";
import DataTableFacetedFilter from "./components/facetedFilter";
import DataTableFilterChips from "./components/filterChips";
import DataTableFilterSheet from "./components/filterSheet";
import DataTablePagination from "./components/pagination";
import DataTableRowActions from "./components/rowActions";
import DataTableSearch from "./components/search";
import DataTableSelectColumn from "./components/selectColumn";
import DataTableSelectionBar from "./components/selectionBar";
import DataTableSpacer from "./components/spacer";
import DataTableToolbar from "./components/toolbar";
import DataTableViewOptions from "./components/viewOptions";
import {
    DataTableSelectionBarContext,
    DataTableSelectionContext,
    DataTableContext
} from "./context";
import {
    getDataTableFacetCounts,
    getDataTableCellContent,
    getDataTableNextSorting,
    getDataTableValueText,
    processDataTableRows
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useStableCallback
} from "../../helpers/interaction";
import {
    usePresence
} from "../../helpers/motion";
import {
    usePointerType,
    useBreakpoint
} from "../../helpers/pointer";
import type {
    TableSize
} from "../table/type";
import {
    PAGINATION_DEFAULT_PAGE_SIZE
} from "../pagination/context";
import {
    getHitArea
} from "../../helpers/theme/control";
import Text from "../text";

const DataTable = <T, >({
    columnVisibility: columnVisibilityProp,
    expandedRowIds: expandedRowIdsProp,
    selectedRowIds: selectedRowIdsProp,
    columnFilters: columnFiltersProp,
    columnWidths: columnWidthsProp,
    globalFilter: globalFilterProp,
    isFirstColumnSticky = false,
    selectionBehaviour = "none",
    expandBehaviour = "single",
    isManualPagination = false,
    isPermissionDenied = false,
    isManualFiltering = false,
    size: sizeProp = "medium",
    layoutBehaviour = "auto",
    onColumnVisibilityChange,
    sortBehaviour = "single",
    isManualSorting = false,
    isHeaderSticky = false,
    onExpandedRowIdsChange,
    pageSize: pageSizeProp,
    onColumnFiltersChange,
    onColumnWidthsChange,
    onGlobalFilterChange,
    sorting: sortingProp,
    isLoading = false,
    onSelectionChange,
    onSelectionDelete,
    renderExpandedRow,
    onPageSizeChange,
    initialPageSize,
    isRowExpandable,
    onSortingChange,
    customLocalize,
    initialSorting,
    page: pageProp,
    error = null,
    onPageChange,
    onSizeChange,
    customTheme,
    getRowLabel,
    renderEmpty,
    onRowPress,
    totalItems,
    children,
    getRowId,
    caption,
    columns,
    onRetry,
    style,
    data,
    ...props
}: IDataTableProps<T>) => {
    const {
        controlPaddings,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        iconSizes,
        radiuses,
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const breakpoint = useBreakpoint();

    const [
        sortingState,
        setSortingState
    ] = useState<Array<IDataTableSortRule>>(initialSorting ?? []);

    const [
        selectedState,
        setSelectedState
    ] = useState<Array<string>>([]);

    const [
        globalFilterState,
        setGlobalFilterState
    ] = useState<string>("");

    const [
        columnFiltersState,
        setColumnFiltersState
    ] = useState<DataTableColumnFilters>({});

    const [
        columnVisibilityState,
        setColumnVisibilityState
    ] = useState<DataTableColumnVisibility>({});

    const [
        columnWidthsState,
        setColumnWidthsState
    ] = useState<DataTableColumnWidths>({});

    const [
        expandedState,
        setExpandedState
    ] = useState<Array<string>>([]);

    const [
        sizeState,
        setSizeState
    ] = useState<TableSize>(sizeProp);

    const [
        pageState,
        setPageState
    ] = useState<number>(1);

    const [
        pageSizeState,
        setPageSizeState
    ] = useState<number>(initialPageSize ?? PAGINATION_DEFAULT_PAGE_SIZE);

    const [
        searchResetKey,
        setSearchResetKey
    ] = useState<number>(0);

    const [
        selectionBarCount,
        setSelectionBarCount
    ] = useState<number>(0);

    const [
        toolbarLayout,
        setToolbarLayout
    ] = useState<DataTableToolbarLayout | undefined>(undefined);

    const [
        paginationCount,
        setPaginationCount
    ] = useState<number>(0);

    const [
        announcement,
        setAnnouncement
    ] = useState<string>("");

    const isShiftPressedRef = useRef<boolean>(false);

    const rangeAnchorRef = useRef<string | undefined>(undefined);

    const {
        container: containerDynamicStyle,
        status: statusDynamicStyle,
        clip: clipDynamicStyle
    } = useStyles({
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors
    });

    const layout: DataTableLayout = layoutBehaviour === "auto" ? (Platform.OS !== "web" || breakpoint === "compact" ? "cards" : "table") : layoutBehaviour;

    const currentSorting = sortingProp ?? sortingState;

    const currentSelectedRowIds = selectedRowIdsProp ?? selectedState;

    const currentGlobalFilter = globalFilterProp ?? globalFilterState;

    const currentColumnFilters = columnFiltersProp ?? columnFiltersState;

    const currentColumnVisibility = columnVisibilityProp ?? columnVisibilityState;

    const currentColumnWidths = columnWidthsProp ?? columnWidthsState;

    const currentExpandedRowIds = expandedRowIdsProp ?? expandedState;

    const currentSize = onSizeChange ? sizeProp : sizeState;

    const currentPageSize = Math.max(pageSizeProp ?? pageSizeState, 1);

    const {
        isRendered: isSelectionBarRendered,
        progress: selectionBarProgress,
        isReduceMotionEnabled
    } = usePresence({
        isVisible: currentSelectedRowIds.length > 0,
        enterIntent: "listInsert",
        exitIntent: "listRemove",
        customTheme
    });

    const isPaginationActive = paginationCount > 0;

    const isExpandEnabled = !!renderExpandedRow && expandBehaviour !== "none" && layout === "table";

    const visibleColumns = useMemo(() => columns.filter((column) => currentColumnVisibility[column.id] !== false && !(layout === "table" && breakpoint === "medium" && (column.priority ?? 1) > 1 && currentColumnVisibility[column.id] === undefined)), [
        currentColumnVisibility,
        breakpoint,
        columns,
        layout
    ]);

    const processedRows = useMemo(() => processDataTableRows({
        columnFilters: currentColumnFilters,
        globalFilter: currentGlobalFilter,
        searchColumns: visibleColumns,
        sorting: currentSorting,
        locale: activeLocale,
        isManualFiltering,
        isManualSorting,
        columns,
        data
    }), [
        currentColumnFilters,
        currentGlobalFilter,
        isManualFiltering,
        isManualSorting,
        visibleColumns,
        currentSorting,
        activeLocale,
        columns,
        data
    ]);

    const getProcessedRowId = useCallback((item: {
        index: number;
        row: T;
    }) => getRowId ? getRowId(item.row, item.index) : String(item.index), [
        getRowId
    ]);

    const totalCount = isManualPagination ? (totalItems ?? processedRows.length) : processedRows.length;

    const pageCount = Math.max(Math.ceil(totalCount / currentPageSize), 1);

    const currentPage = Math.min(Math.max(pageProp ?? pageState, 1), pageCount);

    const pageRows = useMemo(() => {
        if(!isPaginationActive || isManualPagination) {
            return processedRows;
        }

        if(layout === "cards") {
            return processedRows.slice(0, currentPage * currentPageSize);
        }

        return processedRows.slice((currentPage - 1) * currentPageSize, currentPage * currentPageSize);
    }, [
        isPaginationActive,
        isManualPagination,
        currentPageSize,
        processedRows,
        currentPage,
        layout
    ]);

    const rows: Array<DataTableRowModel> = useMemo(() => pageRows.map((item) => {
        const {
            index,
            row
        } = item;

        const id = getProcessedRowId(item);

        const isExpandable = isExpandEnabled && (isRowExpandable ? isRowExpandable(row) : true);

        return {
            detail: isExpandable && currentExpandedRowIds.includes(id) ? renderExpandedRow?.(row, index) : undefined,
            cells: Object.fromEntries(visibleColumns.map((column) => [
                column.id,
                getDataTableCellContent({
                    locale: activeLocale,
                    column,
                    index,
                    row
                })
            ])),
            label: getRowLabel ? getRowLabel(row, index) : id,
            isExpandable,
            id
        };
    }), [
        currentExpandedRowIds,
        renderExpandedRow,
        getProcessedRowId,
        isRowExpandable,
        isExpandEnabled,
        visibleColumns,
        getRowLabel,
        activeLocale,
        pageRows
    ]);

    const selectedSet = useMemo(() => new Set(currentSelectedRowIds), [
        currentSelectedRowIds
    ]);

    const allFilteredRowIds = useMemo(() => processedRows.map(getProcessedRowId), [
        getProcessedRowId,
        processedRows
    ]);

    const selectedCountOnPage = rows.filter((row) => selectedSet.has(row.id)).length;

    const isAllSelected = rows.length > 0 && selectedCountOnPage === rows.length;

    const isSomeSelected = selectedCountOnPage > 0 && !isAllSelected;

    const isAllFilteredSelected = allFilteredRowIds.length > 0 && allFilteredRowIds.every((id) => selectedSet.has(id));

    const facetCounts = useMemo(() => columns.some((column) => column.filterOptions) ? getDataTableFacetCounts({
        columnFilters: currentColumnFilters,
        globalFilter: currentGlobalFilter,
        searchColumns: visibleColumns,
        locale: activeLocale,
        isManualFiltering,
        columns,
        data
    }) : {}, [
        currentColumnFilters,
        currentGlobalFilter,
        isManualFiltering,
        visibleColumns,
        activeLocale,
        columns,
        data
    ]);

    const selectWidth = getHitArea({
        visualSize: iconSizes.small,
        touchTargets,
        pointerType
    }).minWidth + controlPaddings.small * 2;

    const updateSelection = (nextSelectedRowIds: Array<string>) => {
        setSelectedState(nextSelectedRowIds);

        onSelectionChange?.(nextSelectedRowIds);
    };

    const updatePage = (nextPage: number) => {
        setPageState(nextPage);

        onPageChange?.(nextPage);
    };

    const resetPage = () => {
        if(currentPage === 1) {
            return;
        }

        updatePage(1);
    };

    const handleToggleRow = useStableCallback((rowId: string, isRangeProp?: boolean) => {
        if(selectionBehaviour === "none") {
            return;
        }

        const anchorId = rangeAnchorRef.current;

        const isRange = (isRangeProp ?? isShiftPressedRef.current) && selectionBehaviour === "multiple";

        rangeAnchorRef.current = rowId;

        if(isRange && anchorId && anchorId !== rowId) {
            const pageRowIds = rows.map((row) => row.id);

            const anchorIndex = pageRowIds.indexOf(anchorId);

            const targetIndex = pageRowIds.indexOf(rowId);

            if(anchorIndex >= 0 && targetIndex >= 0) {
                const rangeRowIds = pageRowIds.slice(Math.min(anchorIndex, targetIndex), Math.max(anchorIndex, targetIndex) + 1);

                updateSelection([
                    ...currentSelectedRowIds,
                    ...rangeRowIds.filter((id) => !selectedSet.has(id))
                ]);

                return;
            }
        }

        if(selectedSet.has(rowId)) {
            updateSelection(currentSelectedRowIds.filter((id) => id !== rowId));

            return;
        }

        updateSelection(selectionBehaviour === "single" ? [
            rowId
        ] : [
            ...currentSelectedRowIds,
            rowId
        ]);
    });

    const handleTogglePage = useStableCallback(() => {
        const pageRowIds = rows.map((row) => row.id);

        if(isAllSelected) {
            updateSelection(currentSelectedRowIds.filter((id) => !pageRowIds.includes(id)));

            return;
        }

        updateSelection([
            ...currentSelectedRowIds,
            ...pageRowIds.filter((id) => !selectedSet.has(id))
        ]);
    });

    const handleSelectAllRows = useStableCallback(() => {
        updateSelection([
            ...currentSelectedRowIds,
            ...allFilteredRowIds.filter((id) => !selectedSet.has(id))
        ]);
    });

    const handleClearSelection = useStableCallback(() => {
        rangeAnchorRef.current = undefined;

        updateSelection([]);
    });

    const isSelectionDeletable = !!onSelectionDelete;

    const handleDeleteSelection = useStableCallback(() => {
        onSelectionDelete?.(currentSelectedRowIds);

        handleClearSelection();
    });

    const handleSetSorting = useStableCallback((nextSorting: Array<IDataTableSortRule>) => {
        setSortingState(nextSorting);

        onSortingChange?.(nextSorting);
    });

    const handleSort = useStableCallback((columnId: string, isAdditive: boolean) => {
        const column = columns.find((item) => item.id === columnId);

        const nextSorting = getDataTableNextSorting({
            sortFirst: column?.sortFirst,
            behaviour: sortBehaviour,
            sorting: currentSorting,
            isAdditive,
            columnId
        });

        handleSetSorting(nextSorting);

        const primaryRule = nextSorting[0];

        const primaryColumn = columns.find((item) => item.id === primaryRule?.id);

        if(!primaryRule || !primaryColumn) {
            setAnnouncement(localize("data-table-sort-cleared"));

            return;
        }

        setAnnouncement(localize(primaryRule.direction === "asc" ? "data-table-sorted-ascending" : "data-table-sorted-descending", [
            primaryColumn.header
        ]));
    });

    const handleGlobalFilterChange = useStableCallback((nextGlobalFilter: string) => {
        setGlobalFilterState(nextGlobalFilter);

        onGlobalFilterChange?.(nextGlobalFilter);

        resetPage();
    });

    const handleColumnFilterChange = useStableCallback((columnId: string, values: Array<string>) => {
        const nextColumnFilters = Object.fromEntries(Object.entries({
            ...currentColumnFilters,
            [columnId]: values
        }).filter(([
            , columnValues
        ]) => columnValues.length > 0));

        setColumnFiltersState(nextColumnFilters);

        onColumnFiltersChange?.(nextColumnFilters);

        resetPage();
    });

    const handleClearFilters = useStableCallback((isIncludeSearch = false) => {
        setColumnFiltersState({});

        onColumnFiltersChange?.({});

        if(isIncludeSearch) {
            setSearchResetKey((key) => key + 1);

            handleGlobalFilterChange("");

            return;
        }

        resetPage();
    });

    const handleColumnVisibilityChange = useStableCallback((nextColumnVisibility: DataTableColumnVisibility) => {
        setColumnVisibilityState(nextColumnVisibility);

        onColumnVisibilityChange?.(nextColumnVisibility);
    });

    const handleColumnWidthChange = useStableCallback((columnId: string, width: number | undefined) => {
        const nextColumnWidths = Object.fromEntries(Object.entries({
            ...currentColumnWidths,
            [columnId]: width
        }).filter((entry): entry is [string, number] => entry[1] !== undefined));

        setColumnWidthsState(nextColumnWidths);

        onColumnWidthsChange?.(nextColumnWidths);
    });

    const handleSizeChange = useStableCallback((nextSize: TableSize) => {
        setSizeState(nextSize);

        onSizeChange?.(nextSize);
    });

    const handlePageChange = useStableCallback((nextPage: number) => {
        updatePage(Math.min(Math.max(nextPage, 1), pageCount));
    });

    const handlePageSizeChange = useStableCallback((nextPageSize: number) => {
        setPageSizeState(nextPageSize);

        onPageSizeChange?.(nextPageSize);
    });

    const handleToggleExpanded = useStableCallback((rowId: string) => {
        if(!isExpandEnabled) {
            return;
        }

        const isExpanded = currentExpandedRowIds.includes(rowId);

        const nextExpandedRowIds = isExpanded ? currentExpandedRowIds.filter((id) => id !== rowId) : expandBehaviour === "single" ? [
            rowId
        ] : [
            ...currentExpandedRowIds,
            rowId
        ];

        setExpandedState(nextExpandedRowIds);

        onExpandedRowIdsChange?.(nextExpandedRowIds);
    });

    const getExportData = useStableCallback((scope: Exclude<DataTableExportScope, "auto">): DataTableExportData => {
        const exportColumns = columns.filter((column) => currentColumnVisibility[column.id] !== false && !column.isHeaderHidden && !!column.accessor);

        const exportItems = scope === "selected" ? processDataTableRows({
            sorting: currentSorting,
            locale: activeLocale,
            columnFilters: {},
            isManualFiltering,
            globalFilter: "",
            isManualSorting,
            columns,
            data
        }).filter((item) => selectedSet.has(getProcessedRowId(item))) : processedRows;

        return {
            rows: exportItems.map(({
                row
            }) => exportColumns.map((column) => getDataTableValueText(column.accessor?.(row)))),
            columns: exportColumns.map((column) => ({
                header: column.header,
                id: column.id
            }))
        };
    });

    const registerSelectionBar = useCallback(() => {
        setSelectionBarCount((count) => count + 1);

        return () => {
            setSelectionBarCount((count) => count - 1);
        };
    }, []);

    const registerPagination = useCallback(() => {
        setPaginationCount((count) => count + 1);

        return () => {
            setPaginationCount((count) => count - 1);
        };
    }, []);

    useEffect(() => {
        if(!announcement || Platform.OS !== "ios") {
            return;
        }

        AccessibilityInfo.announceForAccessibility(announcement);
    }, [
        announcement
    ]);

    useEffect(() => {
        const doc = Platform.OS === "web" && selectionBehaviour === "multiple" ? globalThis.document : undefined;

        if(!doc) {
            return;
        }

        const onModifierEvent = (event: KeyboardEvent | MouseEvent) => {
            isShiftPressedRef.current = event.shiftKey;
        };

        doc.addEventListener("pointerdown", onModifierEvent, true);

        doc.addEventListener("keydown", onModifierEvent, true);

        doc.addEventListener("keyup", onModifierEvent, true);

        return () => {
            doc.removeEventListener("pointerdown", onModifierEvent, true);

            doc.removeEventListener("keydown", onModifierEvent, true);

            doc.removeEventListener("keyup", onModifierEvent, true);
        };
    }, [
        selectionBehaviour
    ]);

    const context: DataTableContextType = useMemo(() => ({
        columnVisibility: currentColumnVisibility,
        expandedRowIds: currentExpandedRowIds,
        columnFilters: currentColumnFilters,
        columnWidths: currentColumnWidths,
        globalFilter: currentGlobalFilter,
        isExpandable: isExpandEnabled,
        handleColumnVisibilityChange,
        pageSize: currentPageSize,
        handleColumnFilterChange,
        handleGlobalFilterChange,
        columns: visibleColumns,
        handleColumnWidthChange,
        sorting: currentSorting,
        handleDeleteSelection,
        isReduceMotionEnabled,
        handleClearSelection,
        handlePageSizeChange,
        handleToggleExpanded,
        isSelectionDeletable,
        registerSelectionBar,
        allColumns: columns,
        handleSelectAllRows,
        isFirstColumnSticky,
        handleClearFilters,
        isManualPagination,
        isPaginationActive,
        isPermissionDenied,
        registerPagination,
        selectionBehaviour,
        allFilteredRowIds,
        page: currentPage,
        size: currentSize,
        handlePageChange,
        handleSetSorting,
        handleSizeChange,
        handleTogglePage,
        expandBehaviour,
        handleToggleRow,
        customLocalize,
        isHeaderSticky,
        searchResetKey,
        getExportData,
        sortBehaviour,
        customTheme,
        facetCounts,
        renderEmpty,
        selectWidth,
        handleSort,
        onRowPress,
        totalCount,
        isLoading,
        pageCount,
        caption,
        onRetry,
        layout,
        error,
        rows
    }), [
        handleColumnVisibilityChange,
        currentColumnVisibility,
        handleColumnWidthChange,
        handleColumnFilterChange,
        handleGlobalFilterChange,
        currentExpandedRowIds,
        currentColumnFilters,
        isReduceMotionEnabled,
        handleDeleteSelection,
        handleClearSelection,
        handlePageSizeChange,
        handleToggleExpanded,
        isSelectionDeletable,
        currentColumnWidths,
        registerSelectionBar,
        handleSelectAllRows,
        isFirstColumnSticky,
        currentGlobalFilter,
        handleClearFilters,
        isManualPagination,
        isPaginationActive,
        isPermissionDenied,
        registerPagination,
        selectionBehaviour,
        allFilteredRowIds,
        handleTogglePage,
        handleSetSorting,
        handleSizeChange,
        handlePageChange,
        isExpandEnabled,
        currentPageSize,
        searchResetKey,
        handleToggleRow,
        currentSorting,
        visibleColumns,
        customLocalize,
        isHeaderSticky,
        getExportData,
        expandBehaviour,
        sortBehaviour,
        currentSize,
        currentPage,
        renderEmpty,
        customTheme,
        facetCounts,
        selectWidth,
        handleSort,
        onRowPress,
        totalCount,
        isLoading,
        pageCount,
        columns,
        caption,
        onRetry,
        layout,
        error,
        rows
    ]);

    const selectionContext: DataTableSelectionContextType = useMemo(() => ({
        selectedRowIds: currentSelectedRowIds,
        isAllFilteredSelected,
        isSomeSelected,
        isAllSelected
    }), [
        currentSelectedRowIds,
        isAllFilteredSelected,
        isSomeSelected,
        isAllSelected
    ]);

    const isSelectionBarActive = selectionBarCount > 0 && isSelectionBarRendered;

    const selectionBarContext: DataTableSelectionBarContextType = useMemo(() => ({
        isSelectionBarActive,
        selectionBarProgress,
        setToolbarLayout,
        toolbarLayout
    }), [
        selectionBarProgress,
        isSelectionBarActive,
        toolbarLayout
    ]);

    return <DataTableContext.Provider
        value={context}
    >
        <DataTableSelectionContext.Provider
            value={selectionContext}
        >
            <DataTableSelectionBarContext.Provider
                value={selectionBarContext}
            >
                <View
                    {...props}
                    style={[
                        style,
                        stylesheet.container,
                        containerDynamicStyle
                    ]}
                    aria-busy={isLoading}
                >
                    <View
                        style={[
                            stylesheet.clip,
                            clipDynamicStyle
                        ]}
                    >
                        {children ?? <DataTableContent />}
                    </View>
                    <View
                        style={[
                            stylesheet.status,
                            statusDynamicStyle
                        ]}
                        aria-live="polite"
                        role="status"
                    >
                        {
                            announcement ?
                                <Text
                                    customTheme={customTheme}
                                >
                                    {announcement}
                                </Text>
                            :
                                null
                        }
                    </View>
                </View>
            </DataTableSelectionBarContext.Provider>
        </DataTableSelectionContext.Provider>
    </DataTableContext.Provider>;
};
export default Object.assign(DataTable, {
    DensityToggle: DataTableDensityToggle,
    FacetedFilter: DataTableFacetedFilter,
    ColumnHeader: DataTableColumnHeader,
    SelectColumn: DataTableSelectColumn,
    SelectionBar: DataTableSelectionBar,
    FilterChips: DataTableFilterChips,
    FilterSheet: DataTableFilterSheet,
    ViewOptions: DataTableViewOptions,
    Pagination: DataTablePagination,
    RowActions: DataTableRowActions,
    Content: DataTableContent,
    Toolbar: DataTableToolbar,
    Search: DataTableSearch,
    Spacer: DataTableSpacer,
    Export: DataTableExport
});
